/**
 * Avance de obra (4D) de los demos: programa derivado del modelo y avance pieza por pieza.
 *
 * La SECUENCIA sale del modelo: nivel por elevación y, dentro del nivel, grupo de clases IFC (el orden
 * de los grupos y sus semanas son de ejemplo, en `datos/avance-4d.json`). Dentro de una etapa, cada
 * concepto avanza al mismo ritmo que la etapa: con el concepto al X %, sus piezas se completan en orden
 * de posición y la última queda a medias. Así el % del panel y el dibujo son la misma cuenta.
 */
import type { ElementoDemo, ModeloDemo } from './modeloDemo.js';
import datos from './datos/avance-4d.json';
import {
  HP_EJEMPLO,
  cantidadDe,
  coincide,
  conceptoDe,
  crearContexto,
  type ConceptoEjemplo,
  type Selector,
} from './logicaDemo.js';

export type Crece = 'vertical' | 'planta';

interface Grupo {
  id: string;
  nombre: string;
  semanas: number;
  alFinal?: boolean;
  crece: Crece;
  selectores: Selector[];
}

export interface Tramo {
  el: ElementoDemo;
  /** Fracción de la cantidad del concepto (dentro de la etapa) donde empieza y termina esta pieza. */
  desde: number;
  hasta: number;
}

export interface Etapa {
  clave: string;
  nombre: string;
  inicio: number;
  fin: number;
  crece: Crece;
  elementos: ElementoDemo[];
  /** Por concepto: sus piezas en orden de posición, con su tramo de la cantidad. */
  tramos: Tramo[][];
}

export const GRUPOS = datos.grupos as Grupo[];
export const CORTE = datos.corte;

const porPosicion = (a: ElementoDemo, b: ElementoDemo) =>
  (a.caja?.min[0] ?? 0) - (b.caja?.min[0] ?? 0) || (a.caja?.min[2] ?? 0) - (b.caja?.min[2] ?? 0) || (a.caja?.min[1] ?? 0) - (b.caja?.min[1] ?? 0);

export function armarEtapas(modelo: ModeloDemo): Etapa[] {
  const ctx = crearContexto(modelo.elementos);
  const porClave = new Map<string, { orden: [number, number]; nombre: string; els: ElementoDemo[] }>();
  for (const e of modelo.elementos) {
    const gi = GRUPOS.findIndex((g) => g.selectores.some((s) => coincide(e, s, ctx)));
    if (gi < 0) continue;
    const g = GRUPOS[gi]!;
    const nivel = e.nivelOrden ?? Number.MAX_SAFE_INTEGER;
    const clave = g.alFinal ? g.id : `${nivel}|${g.id}`;
    const nombre = g.alFinal
      ? g.nombre
      : `${g.nombre} · nivel ${e.nivelOrden != null ? e.nivelOrden + 1 : '?'}${e.nivelNombre ? ` (${e.nivelNombre})` : ''}`;
    const orden: [number, number] = g.alFinal ? [Number.MAX_SAFE_INTEGER, gi] : [nivel, gi];
    const x = porClave.get(clave) ?? porClave.set(clave, { orden, nombre, els: [] }).get(clave)!;
    x.els.push(e);
  }
  const lista = [...porClave.entries()].sort((a, b) => a[1].orden[0] - b[1].orden[0] || a[1].orden[1] - b[1].orden[1]);
  let t = 0;
  return lista.map(([clave, v]) => {
    const g = GRUPOS[v.orden[1]]!;
    const els = v.els.slice().sort(porPosicion);
    // Reparto por concepto: cada pieza ocupa en su concepto la fracción de su propia cantidad.
    const porConcepto = new Map<string, ElementoDemo[]>();
    for (const e of els) {
      const c = conceptoDe(e, ctx);
      const k = c?.id ?? '-';
      (porConcepto.get(k) ?? porConcepto.set(k, []).get(k)!).push(e);
    }
    const tramos: Tramo[][] = [];
    for (const [k, lista] of porConcepto) {
      const c = HP_EJEMPLO.conceptos.find((x) => x.id === k);
      const peso = (e: ElementoDemo) => (c ? cantidadDe(e, c.magnitud) : 1) || 0;
      const tot = lista.reduce((s, e) => s + peso(e), 0);
      let acum = 0;
      tramos.push(
        lista.map((e, i) => {
          const desde = tot > 0 ? acum / tot : i / lista.length;
          acum += peso(e);
          const hasta = tot > 0 ? acum / tot : (i + 1) / lista.length;
          return { el: e, desde, hasta };
        }),
      );
    }
    const et: Etapa = { clave, nombre: v.nombre, inicio: t, fin: t + g.semanas, crece: g.crece, elementos: els, tramos };
    t += g.semanas;
    return et;
  });
}

/** Avance de cada pieza (0 a 1) en la semana `t`, y la forma en que crece. */
export function avancesEn(etapas: Etapa[], t: number): Map<string, { f: number; crece: Crece }> {
  const m = new Map<string, { f: number; crece: Crece }>();
  for (const et of etapas) {
    const frac = t >= et.fin ? 1 : t <= et.inicio ? 0 : (t - et.inicio) / (et.fin - et.inicio);
    for (const tr of et.tramos) {
      for (const x of tr) {
        const ancho = x.hasta - x.desde;
        const f = frac >= x.hasta ? 1 : frac <= x.desde || ancho <= 0 ? 0 : (frac - x.desde) / ancho;
        m.set(x.el.clave, { f, crece: et.crece });
      }
    }
  }
  return m;
}

/**
 * Dónde se corta una pieza a medio construir: los muros, columnas y cancelería crecen de abajo hacia
 * arriba; las losas y el techo avanzan a lo largo de su lado más largo en planta.
 */
export function corteDe(e: ElementoDemo, f: number, crece: Crece): { eje: 0 | 1 | 2; corte: number } | null {
  if (!e.caja) return null;
  const { min, max } = e.caja;
  if (crece === 'vertical') return { eje: 1, corte: min[1] + f * (max[1] - min[1]) };
  const eje: 0 | 2 = max[0] - min[0] >= max[2] - min[2] ? 0 : 2;
  return { eje, corte: min[eje] + f * (max[eje] - min[eje]) };
}

export interface ConceptoConCantidad {
  c: ConceptoEjemplo;
  els: ElementoDemo[];
  cantidad: number;
  importe: number;
}

/** Conceptos de la HP de ejemplo que el modelo tiene, con su cantidad REAL. */
export function conceptosDelModelo(modelo: ModeloDemo): ConceptoConCantidad[] {
  const ctx = crearContexto(modelo.elementos);
  const grupos = new Map<string, ElementoDemo[]>();
  for (const e of modelo.elementos) {
    const c = conceptoDe(e, ctx);
    if (c) (grupos.get(c.id) ?? grupos.set(c.id, []).get(c.id)!).push(e);
  }
  return HP_EJEMPLO.conceptos
    .filter((c) => grupos.has(c.id))
    .map((c) => {
      const els = grupos.get(c.id)!;
      const cantidad = els.reduce((s, e) => s + cantidadDe(e, c.magnitud), 0);
      return { c, els, cantidad, importe: cantidad * c.pu };
    });
}

/** % programado de cada concepto en la semana `t` (misma cuenta que el dibujo). */
export function programadoPorConcepto(etapas: Etapa[], conceptos: ConceptoConCantidad[], t: number): Map<string, number> {
  const av = avancesEn(etapas, t);
  const out = new Map<string, number>();
  for (const x of conceptos) {
    const tot = x.cantidad || 1;
    const hecho = x.els.reduce((a, e) => a + (av.get(e.clave)?.f ?? 0) * cantidadDe(e, x.c.magnitud), 0);
    out.set(x.c.id, (hecho / tot) * 100);
  }
  return out;
}

/**
 * Avance REPORTADO al corte, pieza por pieza: con el % real de cada concepto (dato de ejemplo), las
 * piezas se completan en el mismo orden del programa y la última queda a medias.
 */
export function avancesReportados(etapas: Etapa[], conceptos: ConceptoConCantidad[]): Map<string, { f: number; crece: Crece }> {
  const real = CORTE.avanceReal as Record<string, number>;
  const creceDe = new Map<string, Crece>();
  for (const et of etapas) for (const e of et.elementos) creceDe.set(e.clave, et.crece);
  const orden = new Map<string, number>();
  etapas.forEach((et, i) => et.elementos.forEach((e, j) => orden.set(e.clave, i * 10000 + j)));
  const m = new Map<string, { f: number; crece: Crece }>();
  for (const x of conceptos) {
    const els = x.els.slice().sort((a, b) => (orden.get(a.clave) ?? 0) - (orden.get(b.clave) ?? 0));
    const tot = x.cantidad || 1;
    let resta = ((real[x.c.id] ?? 0) / 100) * tot;
    for (const e of els) {
      const q = cantidadDe(e, x.c.magnitud);
      const f = q > 0 ? Math.max(0, Math.min(1, resta / q)) : resta > 0 ? 1 : 0;
      resta -= q;
      m.set(e.clave, { f, crece: creceDe.get(e.clave) ?? 'vertical' });
    }
  }
  return m;
}
